export type ProfileHintId = 'profile-photo' | 'headline' | 'location';

export type ProfileHintPlacement = 'top' | 'bottom' | 'inline';

export interface ProfileHintFields {
    profilePhoto?: string | null;
    headline?: string | null;
    location?: { displayText?: string | null } | null;
}

interface ProfileHintDefinition {
    id: ProfileHintId;
    action: string;
    reason: string;
    placement: ProfileHintPlacement;
    isBlank: (profile: ProfileHintFields) => boolean;
}

/**
 * One tip at a time, first blank field in this order.
 * Skills keep their own placeholder, so they are not a tip.
 * Closing a tip snoozes it: a day, then a week, then a month.
 */

export const PROFILE_HINTS: readonly ProfileHintDefinition[] = [
    {
        id: 'profile-photo',
        action: 'Add a photo',
        reason: 'Get more collab requests',
        placement: 'inline',
        isBlank: (profile) => !hasProfilePhoto(profile.profilePhoto),
    },
    {
        id: 'headline',
        action: 'Add a headline',
        reason: 'Lead with what you want',
        placement: 'bottom',
        isBlank: (profile) => !profile.headline?.trim(),
    },
    {
        id: 'location',
        action: 'Add your city',
        reason: 'Find collaborators nearby',
        placement: 'bottom',
        isBlank: (profile) => !profile.location?.displayText?.trim(),
    },
];

const HINT_BY_ID = Object.fromEntries(
    PROFILE_HINTS.map((hint) => [hint.id, hint])
) as Record<ProfileHintId, ProfileHintDefinition>;

const DAY_MS = 24 * 60 * 60 * 1000;

/** First close, second close, then every close after that. */
export const PROFILE_HINT_SNOOZE_MS = [DAY_MS, 7 * DAY_MS, 30 * DAY_MS] as const;

export interface ProfileHintSnooze {
    id: ProfileHintId;
    until: number;
    count: number;
}

/** Same per-user key shape as the header photo cache (`userProfilePhoto_${userId}`). */
const dismissedKey = (userId: string) => `profileHintsDismissed_${userId}`;

const snoozeMs = (count: number) => {
    const index = Math.min(Math.max(count, 1), PROFILE_HINT_SNOOZE_MS.length) - 1;
    return PROFILE_HINT_SNOOZE_MS[index];
};

export function hasProfilePhoto(photo?: string | null) {
    if (!photo || !photo.trim()) return false;
    const value = photo.toLowerCase();
    return !value.includes('default') && !value.includes('placeholder');
}

export function profileHintById(id: ProfileHintId) {
    return HINT_BY_ID[id];
}

export function activeProfileHintId(
    profile: ProfileHintFields,
    snoozed: readonly ProfileHintSnooze[],
    now = Date.now(),
): ProfileHintId | null {
    const hidden = new Set(snoozed.filter((hint) => hint.until > now).map((hint) => hint.id));
    return PROFILE_HINTS.find((hint) => !hidden.has(hint.id) && hint.isBlank(profile))?.id ?? null;
}

const isHintId = (id: unknown): id is ProfileHintId =>
    typeof id === 'string' && id in HINT_BY_ID;

const isSnooze = (item: unknown): item is ProfileHintSnooze => {
    if (!item || typeof item !== 'object') return false;
    const snooze = item as ProfileHintSnooze;
    return isHintId(snooze.id) && typeof snooze.until === 'number' && typeof snooze.count === 'number' && snooze.count > 0;
};

export function readProfileHintSnoozes(userId: string): ProfileHintSnooze[] {
    try {
        const parsed = JSON.parse(localStorage.getItem(dismissedKey(userId)) ?? '[]');
        return Array.isArray(parsed) ? parsed.filter(isSnooze) : [];
    } catch {
        return [];
    }
}

export function dismissProfileHint(userId: string, id: ProfileHintId, now = Date.now()): ProfileHintSnooze[] {
    const current = readProfileHintSnoozes(userId);
    const previous = current.find((hint) => hint.id === id);
    const count = (previous?.count ?? 0) + 1;
    const next = [
        ...current.filter((hint) => hint.id !== id),
        { id, until: now + snoozeMs(count), count },
    ];
    try {
        localStorage.setItem(dismissedKey(userId), JSON.stringify(next));
    } catch {
        // The caller still hides the tip for this visit.
    }
    return next;
}
